import React, { useCallback, useEffect, useState } from 'react';
import { Divider, Grid, Paper, Typography } from '@material-ui/core';
import { DragDropContext } from 'react-beautiful-dnd';
import { v4 as uuid } from 'uuid';
import Dialog from '../../../../catalyst/core/Dialog';
import ToolBox from '../ToolBox';
import { generateUniqueValues } from './helpers';
import { insert, reorder } from '../../utils';
import FormRenderer from '../FormRenderer';
import BuildingSite from '../BuildingSite/BuildingSite';
import tools from '../../constants/tools';
import YesNoActions from '../../../../catalyst/core/Dialog/actions/YesNoActions';
import * as yup from 'yup';

const buildInitialValues = (formfields) => {
    let values = {};

    formfields.forEach((field) => {
        const { name, type } = field;
        if (type) {
            values[name] = '';
        }
    });

    return values;
};

export const buildRenderSchema = (formElements) => {
    const _formElements = [...formElements];
    const formFields = _formElements.map((formElement) => {
        const { component, type, values } = formElement;
        return {
            id: uuid(),
            component,
            type,
            ...values,
        };
    });

    const validation = {};
    const initialValues = buildInitialValues(formFields);

    return {
        formFields,
        validation,
        initialValues,
    };
};

yup.addMethod(yup.string, 'nameIsUnique', (arr) => {
    return yup
        .string()
        .test(
            'uniqueName',
            `name must be unique to the entire Form`,
            (value) => !arr.find((item) => item === value)
        );
});

const FormBuilder = ({ formElements, formElementsRef, setFormElements }) => {
    const [dialogConfig, setDialogConfig] = useState({ open: false });
    const [renderElements, setRenderElements] = useState({
        formFields: [],
        validation: {},
        initialValues: {},
    });

    const generateFormValues = (tool, _formElements) => {
        let values = {};

        tool.form.forEach((schema) => {
            const { name } = schema;
            const value = tool.values[name];
            const _value =
                typeof value === 'string'
                    ? generateUniqueValues(
                          name,
                          value,
                          name === 'name' ? '-' : ' ',
                          _formElements
                      )
                    : value;
            values = { ...values, ...{ [name]: _value } };
        });
        return values;
    };

    const createFormElement = (result, destination, _formElements) => {
        const toolId = result.draggableId.split('-')[1];
        const tool = tools[toolId];
        const { values: _values } = tool;
        const values = _values ? generateFormValues(tool, _formElements) : null;

        const formElement = {
            ...tool,
            ...{ values },
            ...{ id: uuid() },
        };

        setFormElements(insert(_formElements, destination.index, formElement));
    };

    const onDragEnd = useCallback((result) => {
        const { source, destination } = result;
        const _formElements = formElementsRef.current;

        if (!destination) {
            return;
        }

        switch (source.droppableId) {
            case destination.droppableId:
                setFormElements(
                    reorder(_formElements, source.index, destination.index)
                );
                break;
            case 'tool-box':
                createFormElement(result, destination, _formElements);
                break;
        }
    }, []);

    const closeDialog = () => {
        setDialogConfig({
            open: false,
        });
    };

    const editFormElement = (values, item) => {
        closeDialog();
        const _formElements = formElements.map((formElement) =>
            formElement.id === item.id
                ? { ...formElement, ...{ values } }
                : formElement
        );
        setFormElements(_formElements);
    };
    const removeItemOnce = (arr, value) => {
        let index = arr.indexOf(value);
        if (index > -1) {
            arr.splice(index, 1);
        }
        return arr;
    };

    const getFieldNames = (name) => {
        let names = [];
        renderElements.formFields.map((item) => {
            if (item.hasOwnProperty('name')) {
                names.push(item.name);
            }
        });
        return removeItemOnce(names, name);
    };

    const makeValidationSchema = (toolId, name) => {
        let validationSchema = toolId ? tools[toolId].validationSchema : {};
        const nameTools = [4, 5, 6, 7, 8];

        if (nameTools.includes(toolId)) {
            validationSchema.name = yup
                .string()
                .nameIsUnique(getFieldNames(name))
                .matches(
                    /^([A-z0-9_-])*[^\s]\1*$/,
                    'No spaces, only alphanumeric characters and hyphens are allowed'
                )
                .required();
        }
        return validationSchema;
    };

    const editFormElementCallBack = (item) => {
        const { form, values, toolId } = item;
        const { name } = values;
        const validation = makeValidationSchema(toolId, name);
        setDialogConfig({
            open: true,
            title: 'Edit',
            handleClose: closeDialog,
            content: (
                <FormRenderer
                    formFields={form}
                    validationSchema={validation}
                    submit={(data) => editFormElement(data, item)}
                    formType="dialog"
                    initialValues={values}
                    submitText="save"
                    closeDialog={closeDialog}
                />
            ),
        });
    };

    const deleteFormElement = (item) => {
        setFormElements(formElements.filter((f) => f.id !== item.id));
        setDialogConfig({
            open: false,
        });
    };

    const deleteFormElementCallback = (item) => {
        setDialogConfig({
            open: true,
            title: 'Notice',
            description: `Are you sure you wish to permanently delete ${item.label}.`,
            dialogActions: (
                <YesNoActions
                    confirm={() => deleteFormElement(item)}
                    decline={closeDialog}
                />
            ),
            handleClose: closeDialog,
        });
    };

    useEffect(() => {
        setRenderElements(buildRenderSchema(formElements));
    }, [formElements]);

    return (
        <Grid>
            <DragDropContext onDragEnd={onDragEnd}>
                <Grid
                    container
                    direction={'row'}
                    justifyContent="space-evenly"
                    alignItems="flex-start"
                    spacing={1}
                >
                    <Grid item sm={2}>
                        <ToolBox />
                    </Grid>
                    <Grid item sm={5}>
                        <BuildingSite
                            editFormElement={editFormElementCallBack}
                            deleteFormElement={deleteFormElementCallback}
                            formElements={formElements}
                        />
                    </Grid>
                    <Grid item sm={5}>
                        <Paper
                            elevation={0}
                            style={{
                                minHeight: '160px',
                            }}
                        >
                            <Typography variant={'h4'}>Preview</Typography>
                            <Divider style={{ marginBottom: '10px' }} />
                            <FormRenderer
                                formFields={renderElements.formFields}
                                validationSchema={renderElements.validation}
                                submit={() => {}}
                                initialValues={renderElements.initialValues}
                                removeSubmit
                            />
                        </Paper>
                    </Grid>
                </Grid>
            </DragDropContext>
            <Dialog {...dialogConfig} handleClose={closeDialog} />
        </Grid>
    );
};

export default FormBuilder;
